import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Activity, Target, BarChart3, Clock, CheckCircle2, Calendar, Flame, AlertTriangle } from 'lucide-react';

// Score Progress Chart Component
const ScoreProgressChart = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div style={{ 
        height: '200px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8fafc',
        borderRadius: '8px',
        color: '#64748b'
      }}>
        Complete a full-length test to see score progress
      </div>
    );
  }

  // Get min/max scores for scaling
  const allScores = history.flatMap(h => [h.rw_score, h.math_score]).filter(s => s > 0);
  const minScore = Math.max(200, Math.min(...allScores) - 50);
  const maxScore = Math.min(800, Math.max(...allScores) + 50);

  return (
    <div style={{ width: '100%', height: '250px', position: 'relative', padding: '10px 0' }}>
      {/* Y-axis labels - positioned absolutely on the left */}
      <div style={{ 
        position: 'absolute', 
        left: 0, 
        top: '10px', 
        bottom: '45px',
        width: '45px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        paddingRight: '8px'
      }}>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const score = Math.round(minScore + (maxScore - minScore) * (1 - ratio));
          return (
            <span key={`yscore-${ratio}`} style={{ fontSize: '11px', color: '#94a3b8' }}>{score}</span>
          );
        })}
      </div>

      {/* Chart area */}
      <div style={{ marginLeft: '50px', marginRight: '10px', height: '180px', position: 'relative' }}>
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <line key={`grid-${i}`} x1="0" y1={ratio * 100} x2="100" y2={ratio * 100} stroke="#e2e8f0" strokeWidth="0.5" />
          ))}

          {/* RW Line */}
          <path 
            d={history.map((h, i) => {
              const x = (i / (history.length - 1 || 1)) * 100;
              const y = ((maxScore - h.rw_score) / (maxScore - minScore)) * 100;
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ')} 
            fill="none" 
            stroke="#8b5cf6" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          
          {/* Math Line */}
          <path 
            d={history.map((h, i) => {
              const x = (i / (history.length - 1 || 1)) * 100;
              const y = ((maxScore - h.math_score) / (maxScore - minScore)) * 100;
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ')} 
            fill="none" 
            stroke="#0ea5e9" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Data points as HTML elements to avoid stretching */}
        {history.map((h, i) => {
          const xPercent = (i / (history.length - 1 || 1)) * 100;
          const rwYPercent = ((maxScore - h.rw_score) / (maxScore - minScore)) * 100;
          const mathYPercent = ((maxScore - h.math_score) / (maxScore - minScore)) * 100;
          return (
            <div key={h.session_id || `point-${i}`}>
              <div style={{
                position: 'absolute',
                left: `${xPercent}%`,
                top: `${rwYPercent}%`,
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#8b5cf6',
                transform: 'translate(-50%, -50%)',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
              }} />
              <div style={{
                position: 'absolute',
                left: `${xPercent}%`,
                top: `${mathYPercent}%`,
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#0ea5e9',
                transform: 'translate(-50%, -50%)',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
              }} />
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div style={{ 
        marginLeft: '50px', 
        marginRight: '10px', 
        display: 'flex', 
        justifyContent: 'space-between',
        marginTop: '8px'
      }}>
        {history.map((h, i) => {
          const testNum = h.test_name?.replace(/[^0-9]/g, '') || (i + 1);
          return (
            <span key={h.session_id || `label-${i}`} style={{ fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
              Test {testNum}
            </span>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ 
        display: 'flex', 
        gap: '1.5rem', 
        justifyContent: 'center',
        marginTop: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '12px', height: '3px', background: '#8b5cf6', borderRadius: '2px' }} />
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Reading & Writing</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '12px', height: '3px', background: '#0ea5e9', borderRadius: '2px' }} />
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Math</span>
        </div>
      </div>
    </div>
  );
};

// Score Change Badge Component
const ScoreChangeBadge = ({ label, change }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: isNeutral ? '#f1f5f9' : (isPositive ? '#dcfce7' : '#fee2e2'),
      borderRadius: '6px',
      minWidth: '100px'
    }}>
      {isPositive ? <TrendingUp size={14} color="#16a34a" /> : 
       isNeutral ? <Minus size={14} color="#64748b" /> : 
       <TrendingDown size={14} color="#dc2626" />}
      <div>
        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{label}</div>
        <div style={{ 
          fontSize: '0.9rem', 
          fontWeight: '600',
          color: isNeutral ? '#64748b' : (isPositive ? '#16a34a' : '#dc2626')
        }}>
          {isPositive ? '+' : ''}{change}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatItem = ({ icon: Icon, label, value, subtext }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '8px',
    minWidth: '120px'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
      <Icon size={14} color="#64748b" />
      <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>{value}</div>
    {subtext && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{subtext}</div>}
  </div>
);

// Domain Progress Bar Component
const DomainBar = ({ domain, accuracy, status }) => {
  const getStatusColor = (s) => {
    switch (s) {
      case 'Strength': return { bg: '#dcfce7', text: '#16a34a', bar: '#22c55e' };
      case 'Stable': return { bg: '#dbeafe', text: '#2563eb', bar: '#3b82f6' };
      case 'Developing': return { bg: '#fef3c7', text: '#d97706', bar: '#f59e0b' };
      case 'Priority Fix': return { bg: '#fee2e2', text: '#dc2626', bar: '#ef4444' };
      default: return { bg: '#f1f5f9', text: '#64748b', bar: '#94a3b8' };
    }
  };

  const colors = getStatusColor(status);

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#374151' }}>{domain}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>{accuracy}%</span>
          <span style={{ 
            fontSize: '0.65rem', 
            fontWeight: '500',
            padding: '0.15rem 0.4rem',
            borderRadius: '4px',
            background: colors.bg,
            color: colors.text
          }}>
            {status}
          </span>
        </div>
      </div>
      <div style={{ 
        height: '6px', 
        background: '#e2e8f0', 
        borderRadius: '3px',
        overflow: 'hidden'
      }}>
        <div style={{ 
          height: '100%', 
          width: `${Math.min(accuracy, 100)}%`,
          background: colors.bar,
          borderRadius: '3px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );
};

// Stability Badge Component
const StabilityBadge = ({ status }) => {
  const config = {
    stable: { bg: '#dcfce7', text: '#16a34a', label: 'Stable', icon: '🟢' },
    improving: { bg: '#dbeafe', text: '#2563eb', label: 'Improving', icon: '📈' },
    inconsistent: { bg: '#fef3c7', text: '#d97706', label: 'Inconsistent', icon: '🟡' },
    volatile: { bg: '#fee2e2', text: '#dc2626', label: 'Volatile', icon: '🔴' },
    insufficient: { bg: '#f1f5f9', text: '#64748b', label: 'Insufficient Data', icon: '⚪' }
  };

  const { bg, text, label, icon } = config[status] || config.insufficient;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      background: bg,
      color: text,
      borderRadius: '20px',
      fontWeight: '600',
      fontSize: '0.9rem'
    }}>
      <span>{icon}</span>
      <span>{label}</span>
    </div>
  );
};

// Main Performance Insights Component
export default function PerformanceInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/performance-insights`);
      setInsights(response.data);
    } catch (err) {
      console.error('Error fetching performance insights:', err);
      setError('Failed to load performance insights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
        Loading performance insights...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  if (!insights) return null;

  const { score_progress, activity_snapshot, skill_strength, performance_stability } = insights;

  return (
    <div style={{ marginTop: '2rem' }} data-testid="performance-insights">
      {/* Section Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          Overall Performance Overview
        </h2>
        <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
          A cumulative view of your progress across all tests and practice.
        </p>
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0.25rem 0 0 0', fontStyle: 'italic' }}>
          Your 4-Week Plan is generated based on the trends and priorities shown below.
        </p>
      </div>

      {/* Row 1 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr', 
        gap: '1rem',
        marginBottom: '1rem'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'minmax(0, 65fr) minmax(0, 35fr)', 
          gap: '1rem'
        }} className="insights-row-1">
          
          {/* Card 1: Score Progress Over Time */}
          <Card style={{ border: '1px solid #e2e8f0' }}>
            <CardHeader style={{ padding: '1rem 1.25rem 0.5rem', borderBottom: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BarChart3 size={18} color="#6366f1" />
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#1e293b' }}>
                  Score Progress Over Time
                </h3>
              </div>
            </CardHeader>
            <CardContent style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
              <ScoreProgressChart history={score_progress.history} />
              
              {/* Score Changes */}
              <div style={{ 
                display: 'flex', 
                gap: '0.75rem', 
                marginTop: '1rem',
                flexWrap: 'wrap'
              }}>
                <ScoreChangeBadge label="RW Change" change={score_progress.rw_change} />
                <ScoreChangeBadge label="Math Change" change={score_progress.math_change} />
                <ScoreChangeBadge label="Total Change" change={score_progress.total_change} />
              </div>

              {/* Insight */}
              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                background: '#f8fafc',
                borderRadius: '8px',
                borderLeft: '3px solid #6366f1'
              }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                  {score_progress.insight}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Activity Snapshot */}
          <Card style={{ border: '1px solid #e2e8f0' }}>
            <CardHeader style={{ padding: '1rem 1.25rem 0.5rem', borderBottom: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Activity size={18} color="#0ea5e9" />
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#1e293b' }}>
                  Activity Snapshot
                </h3>
              </div>
            </CardHeader>
            <CardContent style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '0.75rem'
              }}>
                <StatItem 
                  icon={CheckCircle2} 
                  label="Questions Completed" 
                  value={activity_snapshot.total_questions} 
                />
                <StatItem 
                  icon={Target} 
                  label="Weighted Accuracy" 
                  value={`${activity_snapshot.weighted_accuracy}%`}
                  subtext="Tests weighted higher"
                />
                <StatItem 
                  icon={BarChart3} 
                  label="Full-Length Tests" 
                  value={activity_snapshot.full_length_tests_completed} 
                />
                <StatItem 
                  icon={Activity} 
                  label="Practice Sets" 
                  value={activity_snapshot.practice_sets_completed} 
                />
                <StatItem 
                  icon={Flame} 
                  label="Study Streak" 
                  value={`${activity_snapshot.current_streak} days`}
                  subtext={activity_snapshot.streak_message}
                />
                <StatItem 
                  icon={Calendar} 
                  label="Last Active" 
                  value={activity_snapshot.last_active} 
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 2 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'minmax(0, 65fr) minmax(0, 35fr)', 
        gap: '1rem'
      }} className="insights-row-2">
        
        {/* Card 3: Cumulative Skill Strength */}
        <Card style={{ border: '1px solid #e2e8f0' }}>
          <CardHeader style={{ padding: '1rem 1.25rem 0.5rem', borderBottom: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Target size={18} color="#22c55e" />
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#1e293b' }}>
                Cumulative Skill Strength
              </h3>
            </div>
          </CardHeader>
          <CardContent style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
            {/* RW Domains */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ 
                fontSize: '0.8rem', 
                fontWeight: '600', 
                color: '#8b5cf6', 
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.75rem'
              }}>
                Reading & Writing Domains
              </h4>
              {skill_strength.rw_domains.map((d, i) => (
                <DomainBar key={d.domain} domain={d.domain} accuracy={d.accuracy} status={d.status} />
              ))}
            </div>

            {/* Math Domains */}
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ 
                fontSize: '0.8rem', 
                fontWeight: '600', 
                color: '#0ea5e9', 
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.75rem'
              }}>
                Math Domains
              </h4>
              {skill_strength.math_domains.map((d, i) => (
                <DomainBar key={d.domain} domain={d.domain} accuracy={d.accuracy} status={d.status} />
              ))}
            </div>

            {/* Summary */}
            <div style={{ 
              padding: '0.75rem 1rem',
              background: '#f8fafc',
              borderRadius: '8px',
              borderLeft: '3px solid #22c55e'
            }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                {skill_strength.summary}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Performance Stability */}
        <Card style={{ border: '1px solid #e2e8f0' }}>
          <CardHeader style={{ padding: '1rem 1.25rem 0.5rem', borderBottom: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={18} color="#f59e0b" />
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#1e293b' }}>
                Performance Stability
              </h3>
            </div>
          </CardHeader>
          <CardContent style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
            {/* Stability Badge */}
            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <StabilityBadge status={performance_stability.status} />
            </div>

            {/* Sub-metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: '#f8fafc',
                borderRadius: '6px'
              }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Recent Score Variability</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
                  ±{performance_stability.score_variability}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: '#f8fafc',
                borderRadius: '6px'
              }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Domain Balance</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
                  {performance_stability.domain_balance}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: '#f8fafc',
                borderRadius: '6px'
              }}>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Time Pressure Rate</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
                  {performance_stability.time_pressure_rate}%
                </span>
              </div>
            </div>

            {/* Insight */}
            <div style={{ 
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              background: '#f8fafc',
              borderRadius: '8px',
              borderLeft: '3px solid #f59e0b'
            }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                {performance_stability.insight}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 900px) {
          .insights-row-1, .insights-row-2 {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
